import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

/* eslint-disable react/no-danger */
import DOMPurify from 'dompurify';
import CompanyLogo from '../../components/CompanyLogo';
import Text from '../../components/Text';
import { LanguageContext } from '../../config/LanguageContext';
import { getTerms } from '../../services/termsService';
import { getPublicClientConfig } from '../../services/truststartService';
import agreeingToTerms from './agreeingToTerms';

const SCROLL_END_THRESHOLD_PX = 8;
const TERMS_LOAD_ERROR_KEY = 'Terms Load Error';
const TERMS_MISSING_CLIENT_ID_KEY = 'Terms Missing Client Id Error';

export default function CustomInviteAgreement(props) {
  const { onAgreementAccepted, isGuestUser, termsClientId } = props;
  const { language } = useContext(LanguageContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsHtml, setTermsHtml] = useState('');
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const [clientConfig, setClientConfig] = useState(null);
  const termsContainerRef = useRef(null);

  const setReachedEndFromContainer = useCallback(() => {
    const container = termsContainerRef.current;
    if (!container || hasReachedEnd) {
      return;
    }

    const isScrollable = (
      container.scrollHeight - container.clientHeight
      > SCROLL_END_THRESHOLD_PX
    );
    if (!isScrollable) {
      setHasReachedEnd(true);
      return;
    }

    const hasScrolledToEnd = (
      container.scrollTop + container.clientHeight
      >= container.scrollHeight - SCROLL_END_THRESHOLD_PX
    );
    if (hasScrolledToEnd) {
      setHasReachedEnd(true);
    }
  }, [hasReachedEnd]);

  const loadTerms = useCallback(async () => {
    if (!termsClientId) {
      setErrorKey(TERMS_MISSING_CLIENT_ID_KEY);
      setIsLoading(false);
      return;
    }

    setErrorKey('');
    setIsLoading(true);
    setHasReachedEnd(false);

    try {
      const result = await getTerms(termsClientId, language);
      if (!result?.content || !result.content.trim()) {
        setErrorKey(TERMS_LOAD_ERROR_KEY);
        setTermsHtml('');
        return;
      }
      setTermsHtml(DOMPurify.sanitize(result.content));
    } catch (error) {
      setErrorKey(TERMS_LOAD_ERROR_KEY);
      setTermsHtml('');
      // eslint-disable-next-line no-console
      console.error('Failed to fetch client terms:', { termsClientId, language, error });
    } finally {
      setIsLoading(false);
    }
  }, [language, termsClientId]);

  const acceptTerms = useCallback(() => (
    agreeingToTerms(false, isGuestUser, language)
      .then((guestUserData) => {
        onAgreementAccepted(guestUserData);
      })
  ), [isGuestUser, language, onAgreementAccepted]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  useEffect(() => {
    if (!termsClientId) {
      return;
    }

    getPublicClientConfig(termsClientId).then(setClientConfig);
  }, [termsClientId]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(setReachedEndFromContainer);
    const timeoutId = window.setTimeout(setReachedEndFromContainer, 0);
    const onResize = () => setReachedEndFromContainer();
    window.addEventListener('resize', onResize);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', onResize);
    };
  }, [termsHtml, setReachedEndFromContainer]);

  const onAgreeAndContinue = () => {
    if (!hasReachedEnd || !termsHtml || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    acceptTerms()
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to save terms agreement:', { termsClientId, language, error });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (isLoading) {
    return (
      <div className="container">
        <p><Text value="Loading..." /></p>
      </div>
    );
  }

  if (errorKey) {
    return (
      <div className="container">
        <h3><Text value="Terms and Conditions" /></h3>
        <p><Text value={errorKey} /></p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={loadTerms}
          data-cy="retryTermsButton"
        >
          <Text value="Try Again" />
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      {clientConfig?.dealerIBV?.logo && (
        <div className="logo-container">
          <CompanyLogo
            size="large"
            companyLogo={clientConfig.dealerIBV.logo}
            clientId={termsClientId}
            companyDisplayName={clientConfig.dealerIBV.displayName}
          />
        </div>
      )}
      <p className="agree-label"><Text value="AgreeToConfirm" /></p>
      <div
        className="termsConsentContainer"
        ref={termsContainerRef}
        onScroll={setReachedEndFromContainer}
      >
        <div
          className="client-terms-content"
          dangerouslySetInnerHTML={{ __html: termsHtml }}
        />
      </div>

      <div className="d-flex w-100 flex-row justify-content-center mt-3">
        <button
          type="button"
          className="btn btn-success"
          onClick={onAgreeAndContinue}
          data-cy="nextButton"
          aria-label="Agree and Continue"
          disabled={!hasReachedEnd || isSubmitting}
        >
          <Text value="AgreeAndContinue" />
        </button>
      </div>
    </div>
  );
}
